// The ONE chart primitive for every Netwatch surface — chat blocks, Outlook
// disk trends, projections — rendered identically in the dashboard SPA and
// inside the widget's shadow DOM.
//
// It is deliberately thin: chartSpec decided what to draw, chartOptions decided
// how, chartDecor draws what Chart.js won't, and chartTheme resolves the
// tokens. This file only registers Chart.js pieces, wires those four together
// and owns the canvas.

import { useCallback, useMemo, useRef, useState } from 'react'
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  DoughnutController,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PieController,
  PointElement,
  ScatterController,
  Tooltip,
} from 'chart.js'
import { Chart } from 'react-chartjs-2'
import type { Chart as ChartJSInstance } from 'chart.js'

import { buildChartData, buildChartOptions } from './chartOptions'
import { nwDecor, type DecorExtras } from './chartDecor'
import { PALETTE_FALLBACKS, PALETTE_VARS, describeChart, zoomedSpec, type ChartSpec } from './chartSpec'
import { DEFAULT_TOKENS, useChartTokens } from './chartTheme'

// One registration list for the whole app. LineController is easy to miss:
// react-chartjs-2's typed <Line> registers it implicitly, but the generic
// <Chart type=…> below does not — without it every existing line chart dies.
ChartJS.register(
  // line + scatter
  LineController, LineElement, PointElement, Filler, ScatterController,
  // bar
  BarController, BarElement,
  // pie + doughnut (PieController extends DoughnutController)
  ArcElement, DoughnutController, PieController,
  // scales + chrome. No TimeScale: there is no date adapter installed, so
  // time series ride a linear axis with our own tick formatter.
  LinearScale, CategoryScale, Tooltip, Legend,
)

/** Pointer travel below this is a click (a tooltip tap), not a zoom drag. */
const DRAG_THRESHOLD_PX = 6

export interface NwChartProps {
  spec: ChartSpec
  ariaLabel?: string
  /** Height override; defaults to the per-type height the spec computed. */
  height?: number
  /** Extra decoration the spec can't know about (Outlook's "now" divider). */
  decor?: DecorExtras
  /** Handed the live chart plus the resolved surface colour once it mounts, so
   *  a parent can snapshot the canvas (PNG export needs both). */
  onReady?: (chart: ChartJSInstance, surface: string) => void
  /** Drag across the plot to zoom the x axis into that window. Off by default:
   *  in a chat bubble a drag is how you select text, and the chart is too small
   *  to aim at anyway — the expanded view turns it on. */
  zoomable?: boolean
  /** Token names for chart chrome, resolved against this chart's container. */
  inkVar?: string
  faintVar?: string
  gridVar?: string
  thresholdVar?: string
  surfaceVar?: string
}

export function NwChart({
  spec,
  ariaLabel,
  height,
  decor,
  onReady,
  zoomable = false,
  inkVar = DEFAULT_TOKENS.ink[0],
  faintVar = DEFAULT_TOKENS.faint[0],
  gridVar = DEFAULT_TOKENS.grid[0],
  thresholdVar = DEFAULT_TOKENS.threshold[0],
  surfaceVar = DEFAULT_TOKENS.surface[0],
}: NwChartProps) {
  // Arc charts colour by SLICE, so they need the whole palette resolved even
  // though they only have one series.
  const seriesTokens = useMemo(() => {
    if (spec.type === 'pie' || spec.type === 'doughnut') {
      return PALETTE_VARS.map((name, i) => [name, PALETTE_FALLBACKS[i]] as const)
    }
    return spec.series.map((s) => [s.colorVar, s.fallback] as const)
  }, [spec])

  const { wrapRef, colors } = useChartTokens({
    inkVar, faintVar, gridVar, thresholdVar, surfaceVar, series: seriesTokens,
  })

  // Zoom lives here rather than in the spec so the caller keeps one object and
  // the reader keeps every point: only the axis window moves.
  const [range, setRange] = useState<{ from: number; to: number } | null>(null)
  // `startPx` is what separates a zoom from a click: a tooltip tap moves the
  // pointer by a pixel or two, and zooming to that instant would be a jump the
  // reader never asked for.
  const [drag, setDrag] = useState<{ from: number; to: number; startPx: number } | null>(null)
  const chartRef = useRef<ChartJSInstance | null>(null)
  const canZoom = zoomable && !spec.categorical && spec.type !== 'pie' && spec.type !== 'doughnut'

  const dataXAt = useCallback((clientX: number): number | null => {
    const chart = chartRef.current
    if (!chart) return null
    const box = chart.canvas.getBoundingClientRect()
    const x = clientX - box.left
    const area = chart.chartArea
    if (!area) return null
    const clamped = Math.max(area.left, Math.min(x, area.right))
    const value = chart.scales.x?.getValueForPixel(clamped)
    return typeof value === 'number' ? value : null
  }, [])

  const view = useMemo(
    () => (range ? zoomedSpec(spec, range.from, range.to) : spec),
    [spec, range],
  )

  const data = useMemo(() => buildChartData(view, colors), [view, colors])
  const options = useMemo(() => buildChartOptions(view, colors), [view, colors])
  const plugins = useMemo(
    () => [nwDecor(view, colors, { ...decor, selection: drag })],
    [view, colors, decor, drag],
  )

  return (
    <div
      ref={wrapRef}
      style={{ position: 'relative', height: height ?? spec.height, cursor: canZoom ? 'crosshair' : undefined }}
      onPointerDown={canZoom ? (ev) => {
        if (ev.button !== 0) return
        const from = dataXAt(ev.clientX)
        if (from == null) return
        ev.currentTarget.setPointerCapture(ev.pointerId)
        setDrag({ from, to: from, startPx: ev.clientX })
      } : undefined}
      onPointerMove={canZoom && drag ? (ev) => {
        const to = dataXAt(ev.clientX)
        if (to != null) setDrag({ ...drag, to })
      } : undefined}
      onPointerUp={canZoom && drag ? (ev) => {
        const to = dataXAt(ev.clientX) ?? drag.to
        const travelled = Math.abs(ev.clientX - drag.startPx)
        setDrag(null)
        if (travelled < DRAG_THRESHOLD_PX) return
        // Zoom relative to what is on screen, so a second drag narrows further
        // instead of re-reading the full window.
        const target = zoomedSpec(view, drag.from, to)
        if (target.xMin != null && target.xMax != null) setRange({ from: target.xMin, to: target.xMax })
      } : undefined}
      onPointerCancel={canZoom && drag ? () => setDrag(null) : undefined}
      onDoubleClick={canZoom && range ? () => setRange(null) : undefined}
    >
      <Chart
        ref={(instance) => {
          const chart = (instance ?? null) as ChartJSInstance | null
          chartRef.current = chart
          if (chart) onReady?.(chart, colors.surface)
        }}
        type={view.type}
        data={data}
        options={options}
        plugins={plugins}
        aria-label={ariaLabel || spec.title || 'chart'}
        role="img"
        fallbackContent={describeChart(view)}
      />
      {canZoom && range && (
        <button
          type="button"
          className="nw-chart-zoom-reset"
          onClick={() => setRange(null)}
          aria-label="Reset zoom"
        >
          Reset zoom
        </button>
      )}
    </div>
  )
}
